import { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Typography } from '@components/Typography';
import ImportValidationTable from '@components/ImportValidationTable';
import { useTranslation } from '@hooks';
import { CHANNEL } from '@constants/channels';
import { FullScreenModal, cellStyles } from './styles';

const cell = (value, color = 'textPrimary') => (
    <Typography color={color} variant="body2" align="left" style={cellStyles}>
        {value || '--'}
    </Typography>
);

const GetContactsColumnNames = () => {
    const { t } = useTranslation();
    return [
        { key: 0, component: <Typography variant="subtitle2">{t('SendInvites.id')}</Typography> },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.firstName')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.lastName')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.phoneNumber')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.emailAddress')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.facebookProfile')}
                </Typography>
            ),
        },
        {
            key: 6,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.instagramProfile')}
                </Typography>
            ),
        },
        {
            key: 7,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.externalId')}
                </Typography>
            ),
        },
        { key: 8, component: <Typography align="left" variant="subtitle2"></Typography> },
    ];
};

const getContactsRows = (channelType, contacts, onDeleteContact) => {
    const isEmail = channelType === CHANNEL.EMAIL;
    return contacts.map((contact, index) => {
        const { firstName, lastName, phoneNumber, email, facebook, instagram, externalId, phoneValid, emailValid } =
            contact;
        const isInvalid = isEmail ? !emailValid : !phoneValid;
        const phoneColor = !isEmail && !phoneValid && phoneNumber ? 'error' : 'textPrimary';
        const emailColor = isEmail && !emailValid && email ? 'error' : 'textPrimary';
        const nameColor = isInvalid ? 'error' : 'textPrimary';

        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left" sx={{ maxWidth: '56px' }}>
                        {index + 1}
                    </Typography>
                ),
            },
            { component: cell(firstName, nameColor) },
            { component: cell(lastName, nameColor) },
            { component: cell(phoneNumber, phoneColor) },
            { component: cell(email, emailColor) },
            { component: cell(facebook) },
            { component: cell(instagram) },
            { component: cell(externalId) },
            {
                component: (
                    <IconButton
                        sx={{ color: isInvalid ? 'var(--error-main)' : 'var(--text-secondary)' }}
                        onClick={() => onDeleteContact(index)}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            },
        ];
    });
};

const DataVerificationModal = ({ data, setData, channelType, isOpen, onClose }) => {
    const [isValid, setIsValid] = useState(false);
    const { t } = useTranslation();
    const firstRender = useRef(true);

    useEffect(() => {
        if (data.list.length > 0 && firstRender.current) {
            setIsValid(validateList(data.list));
            firstRender.current = false;
        }
    }, [data]);

    const validateList = people => {
        return channelType === CHANNEL.EMAIL
            ? people.every(person => person.emailValid)
            : people.every(person => person.phoneValid);
    };

    const onDeleteContact = newValue => {
        const contactsCopy = [...data.list];
        contactsCopy.splice(newValue, 1);
        setData(prev => ({ ...prev, list: contactsCopy }));
        setIsValid(validateList(contactsCopy));
    };

    const handleClose = () => {
        setData(prev => ({ ...prev, list: [] }));
        onClose();
    };

    const handleSubmit = () => {
        setData(prev => ({ ...prev, list: [], selected: prev.list }));
        onClose();
    };

    return (
        <FullScreenModal
            fullScreen
            isOpen={isOpen}
            handleClose={handleClose}
            title={t('SendInvites.dataVerification')}
            onPrimaryAction={handleSubmit}
            primaryActionText={t('buttons.import', { amount: data.length })}
            primaryActionDisabled={!isValid}
        >
            <Typography
                variant="caption"
                color="textSecondary"
                sx={{ marginBottom: '24px', display: 'block', color: 'var(--text-secondary)' }}
            >
                {t('SendInvites.dataVerificationDescription')}
            </Typography>
            <ImportValidationTable
                columnNames={GetContactsColumnNames()}
                rows={getContactsRows(channelType, data.list, onDeleteContact)}
                hasError={data.list.map(person =>
                    channelType === CHANNEL.EMAIL ? !person.emailValid : !person.phoneValid,
                )}
            />
        </FullScreenModal>
    );
};

export default DataVerificationModal;
